import { HttpClient } from "@angular/common/http";
import { Injectable } from '@angular/core';
import { firstValueFrom } from "rxjs";
import { LocalizedMinResult, MinResult } from 'src/app/shared/models/result/result.interface';
import { CookieService } from 'src/app/core/services/cookie.service';
import { environment } from 'src/environments/environment';
import { ErrorService } from './error.service';
import { TranslateService } from "@ngx-translate/core";

@Injectable({
  providedIn: 'root',
})
export class FavoritesService {
  public favoriteIds: any = {};
  public tmpId = -1;

  constructor(
    private cookies: CookieService,
    private http: HttpClient,
    private errors: ErrorService,
    private translate: TranslateService,
  ) {
    if (this.getToken()) {
      this.getFavoritesServer()
    }
    else {
      const tmp = this.cookies.getCookie('favorites');
      this.favoriteIds = this.safeParseFavoriteIds(tmp);
    }
  }

  async getFavoritesServer() {
    let token = localStorage.getItem('token');
    if (token == null)
      token = '';
    const list = await firstValueFrom(
      this.http.get<any[]>(environment.apiUrl + '/results/favorite/min?language='+this.translate.currentLang, {
        params: {
          user: token
        }
      })
    )
    this.favoriteIds = {};
    list.forEach(result => {
      this.favoriteIds[result.v_id] = true;
    });
  }

  get request(): string[] {
    return Object.keys(this.favoriteIds).filter((s: string) => s.length > 20);
  }

  private getToken(): string | null {
    return localStorage.getItem('token');
  }

  get favorites() {
    if (
      !Object.keys(this.favoriteIds).length &&
      this.cookies.rights?.save_preferences &&
      this.cookies.getCookie('favorites')
    ) {
      this.favoriteIds = this.safeParseFavoriteIds(
        this.cookies.getCookie('favorites')
      );
    }
    return this.favoriteIds;
  }

  /** Corrupt cookie JSON would throw and break app bootstrap (white screen). */
  private safeParseFavoriteIds(raw: string): any {
    if (!raw) return {};
    try {
      const parsed = JSON.parse(raw);
      return parsed && typeof parsed === 'object' && !Array.isArray(parsed)
        ? parsed
        : {};
    } catch {
      this.cookies.deleteCookie('favorites');
      return {};
    }
  }

  public changeFavorite(result: LocalizedMinResult) {
    if (!this.cookies.rights?.save_preferences) {
      this.cookies.neededRightsChange = {
        save_preferences: true,
        save_filters: false,
        data_consent: false,
        google_analytics: false,
        data_protection: false,
      };
      return;
    }

    if (result.v_id in this.favoriteIds && this.favoriteIds[result.v_id] === true)
      this.sendFavoriteUpdate('delete', result)
    else
      this.sendFavoriteUpdate('add', result)

    this.saveFavoritesToCookies();
  }

  async sendFavoriteUpdate(type: 'add' | 'delete', result: LocalizedMinResult) {
    const url = environment.apiUrl + `/results/favorite/${result._id}`;

    switch (type) {
      case 'add':
        this.favoriteIds[result.v_id] = true;
        if (this.getToken()) {
          return firstValueFrom(
            this.http.post(url, null)
          ).catch((error) => {
            this.errors.updateServerError();
            throw error;
          });
        }
        return
      case 'delete':
        delete this.favoriteIds[result.v_id];
        if (this.getToken()) {
          return firstValueFrom(
            this.http.delete(url)
          ).catch((error) => {
            this.errors.updateServerError();
            throw error;
          });
        }
        return
      default:
        return
    }
  }

  private saveFavoritesToCookies(): void {
    this.cookies.setCookie({
      name: 'favorites',
      value: JSON.stringify(this.favoriteIds),
      expireDays: 365
    });
  }

  public isFavorite(result: LocalizedMinResult): boolean {
    return result.v_id in this.favoriteIds
  }

  reset() {
    this.cookies.rights = undefined;
    this.cookies.deleteCookie('favorites');
  }
}
